import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Box, Minus, PackageCheck, Plus, Search, ShieldCheck, ShoppingCart, Trash2, Truck } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";

function money(value) {
  return `${Number(value || 0).toLocaleString("tr-TR")} TL`;
}

function StockBadge({ stock }) {
  const count = Number(stock || 0);
  const tone = count <= 0 ? "bg-red-50 text-red-700 ring-red-100" : count <= 5 ? "bg-amber-50 text-amber-700 ring-amber-100" : "bg-emerald-50 text-emerald-700 ring-emerald-100";
  return <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1 ${tone}`}>{count <= 0 ? "Stok yok" : `${count} stokta`}</span>;
}

export default function CartPage(){
  const {cart,cartTotal,updateQuantity,removeFromCart,getMinQty}=useShop();
  const {isCorporate,isAdmin}=useAuth();
  const [query, setQuery] = useState("");
  const shipping=cartTotal>=2500||cartTotal===0?0:149;
  const total=cartTotal+shipping;
  const minQty = getMinQty();

  const filteredCart = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr-TR");
    if (!q) return cart;
    return cart.filter((item) => [item.name, item.type, item.usage, item.series].join(" ").toLocaleLowerCase("tr-TR").includes(q));
  }, [cart, query]);

  const cartStats = useMemo(() => ({
    productCount: cart.length,
    pieceCount: cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    freeShippingLeft: Math.max(0, 2500 - cartTotal),
  }), [cart, cartTotal]);

  if(isAdmin)return <section className="px-5 md:px-10 py-16 text-center"><h1 className="text-3xl font-bold mb-3">Admin sipariş veremez</h1><p className="text-stone-500 mb-6">Admin hesabı sadece gelen siparişleri ve ürünleri yönetir.</p><Link to="/admin" className="bg-stone-900 text-white px-6 py-3 rounded-xl">Ürün Yönetimine Git</Link></section>;

  if(!cart.length)return (
    <section className="px-4 py-10 sm:px-6 md:px-10">
      <div className="mx-auto max-w-3xl overflow-hidden rounded-[2rem] bg-white p-10 text-center shadow-soft ring-1 ring-stone-100">
        <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-stone-50 text-stone-500"><ShoppingCart size={36}/></div>
        <p className="mb-2 text-sm font-bold uppercase tracking-[0.25em] text-emerald-700">Sevinç Outdoor</p>
        <h1 className="text-3xl font-black sm:text-4xl">Sepetin boş</h1>
        <p className="mx-auto mt-3 max-w-xl text-stone-500">Kamp, outdoor ve bıçak ürünlerini sepetine ekleyerek hızlıca sipariş oluşturabilirsin.</p>
        <Link to="/products" className="mt-7 inline-flex items-center gap-2 rounded-2xl bg-stone-950 px-6 py-4 font-bold text-white">Ürünlere Git <ArrowRight size={18}/></Link>
      </div>
    </section>
  );

  return (
    <section className="px-4 py-8 sm:px-6 md:px-10">
      <div className="mb-7 overflow-hidden rounded-[2rem] bg-gradient-to-br from-stone-950 via-stone-900 to-emerald-950 p-6 text-white shadow-soft sm:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-emerald-200">Güvenli Sipariş</p>
            <h1 className="text-3xl font-black sm:text-4xl">Sepetim</h1>
            <p className="mt-2 max-w-2xl text-sm text-stone-300">Ürünlerini kontrol et, adetleri stok sınırına göre düzenle ve teslimat adımına profesyonel sipariş özetiyle devam et.</p>
          </div>
          <Link to="/products" className="w-fit rounded-2xl bg-white px-5 py-3 text-sm font-bold text-stone-950">Alışverişe Devam Et</Link>
        </div>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <div className="rounded-3xl bg-white p-5 shadow-soft"><p className="text-sm text-stone-500">Ürün Çeşidi</p><strong className="mt-1 block text-2xl">{cartStats.productCount}</strong></div>
        <div className="rounded-3xl bg-white p-5 shadow-soft"><p className="text-sm text-stone-500">Toplam Adet</p><strong className="mt-1 block text-2xl">{cartStats.pieceCount}</strong></div>
        <div className="rounded-3xl bg-white p-5 shadow-soft"><p className="text-sm text-stone-500">Kargo Avantajı</p><strong className="mt-1 block text-lg">{shipping === 0 ? "Ücretsiz Kargo" : `${money(cartStats.freeShippingLeft)} kaldı`}</strong></div>
      </div>

      {isCorporate&&<div className="mb-6 flex gap-3 rounded-3xl border border-emerald-100 bg-emerald-50 p-4 text-emerald-800"><ShieldCheck className="shrink-0"/><p><strong>Kurumsal hesap aktif.</strong> Kurumsal özel fiyatlar uygulanır ve minimum ürün adedi {minQty}’dur.</p></div>}

      <div className="grid gap-8 lg:grid-cols-[1.8fr_0.9fr]">
        <div className="space-y-5">
          <div className="relative rounded-3xl bg-white p-4 shadow-soft">
            <Search size={18} className="absolute left-8 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Sepette ürün ara" className="w-full rounded-2xl border border-stone-200 py-3 pl-11 pr-4 outline-none focus:border-emerald-600" />
          </div>

          {!filteredCart.length ? <div className="rounded-3xl bg-white p-10 text-center shadow-soft"><Box className="mx-auto mb-4 text-stone-400" size={44}/><h2 className="text-xl font-bold">Aramana uygun ürün yok</h2></div> : filteredCart.map(i=>{
            const max=Number(i.stock||0);
            const lineTotal = Number(i.unitPrice||i.price) * Number(i.quantity||0);
            return <article key={i.id} className="overflow-hidden rounded-[1.8rem] bg-white shadow-soft ring-1 ring-stone-100">
              <div className="flex flex-col gap-5 p-5 md:flex-row">
                <img src={i.image} alt={i.name} className="h-56 w-full rounded-3xl object-cover md:h-40 md:w-44"/>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="text-xl font-black text-stone-950">{i.name}</h3>
                      <p className="mt-1 text-sm text-stone-500">{i.type}{i.usage ? ` · ${i.usage}` : ""}</p>
                    </div>
                    <StockBadge stock={max}/>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl bg-stone-50 p-3"><p className="text-xs text-stone-500">Birim Fiyat</p><strong>{money(i.unitPrice||i.price)}</strong></div>
                    <div className="rounded-2xl bg-stone-50 p-3"><p className="text-xs text-stone-500">Adet</p><strong>{i.quantity}</strong></div>
                    <div className="rounded-2xl bg-stone-50 p-3"><p className="text-xs text-stone-500">Ürün Tutarı</p><strong>{money(lineTotal)}</strong></div>
                  </div>

                  <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="inline-flex w-fit items-center gap-3 rounded-2xl border border-stone-200 bg-white p-2">
                      <button disabled={i.quantity<=minQty} onClick={()=>updateQuantity(i.id,i.quantity-1)} className="rounded-xl bg-stone-100 p-2 disabled:opacity-40"><Minus size={17}/></button>
                      <span className="min-w-8 text-center font-black">{i.quantity}</span>
                      <button disabled={i.quantity>=max} onClick={()=>updateQuantity(i.id,i.quantity+1)} className="rounded-xl bg-stone-100 p-2 disabled:opacity-40"><Plus size={17}/></button>
                    </div>
                    <span className="text-xs text-stone-500">Min: {minQty} · Maks: {max}</span>
                    <button onClick={()=>removeFromCart(i.id)} className="sm:ml-auto inline-flex items-center gap-2 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700"><Trash2 size={17}/> Sepetten Kaldır</button>
                  </div>
                </div>
              </div>
            </article>
          })}
        </div>

        <aside className="h-fit rounded-[1.8rem] bg-white p-6 shadow-soft ring-1 ring-stone-100 lg:sticky lg:top-24">
          <div className="mb-5 flex items-center gap-3"><div className="rounded-2xl bg-emerald-50 p-3 text-emerald-700"><PackageCheck/></div><div><h2 className="text-2xl font-black">Sipariş Özeti</h2><p className="text-sm text-stone-500">Kontrol ve ödeme adımı</p></div></div>
          <div className="space-y-3 rounded-3xl bg-stone-50 p-4">
            <div className="flex justify-between"><span>Ara Toplam</span><strong>{money(cartTotal)}</strong></div>
            <div className="flex justify-between"><span>Kargo</span><strong>{shipping===0?"Ücretsiz":money(shipping)}</strong></div>
            <div className="border-t border-stone-200 pt-3 flex justify-between font-black text-xl"><span>Toplam</span><span>{money(total)}</span></div>
          </div>
          {shipping !== 0 && <div className="mt-4 flex gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-amber-800"><Truck size={18} className="shrink-0"/> 2.500 TL ve üzeri siparişlerde kargo ücretsiz.</div>}
          {cart.some((item) => Number(item.quantity || 0) > Number(item.stock || 0)) && <div className="mt-4 flex gap-2 rounded-2xl bg-red-50 p-3 text-sm text-red-700"><AlertTriangle size={18} className="shrink-0"/> Sepette stok sınırını aşan ürün var.</div>}
          <Link to="/checkout" className="mt-6 flex items-center justify-center gap-2 rounded-2xl bg-emerald-700 py-4 font-bold text-white hover:bg-emerald-800">Ödemeye Geç <ArrowRight size={18}/></Link>
          <p className="mt-4 text-center text-xs text-stone-500">Stok kontrolü sipariş oluşturulurken tekrar yapılır.</p>
        </aside>
      </div>
    </section>
  );
}
